import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
    Crown,
    BarChart3,
    Calendar,
    FilePlus,
    LayoutDashboard,
    BookOpen,
    Plug,
    Download,
    Share2,
    ListPlus,
    PieChart,
    PlugZap,
    ShoppingBag,
    Sparkles,
} from 'lucide-react';
import { usePlanFeaturesStore } from '../../stores/planFeaturesStore';
import { subscriptionsService } from '../../lib/api/subscriptions';
import { buildShopifyManageBillingUrl } from '../../lib/shopifyAdminUrl';

// ═══════════════════════════════════════════════════
// Icon mapping (lucide icon name → component)
// ═══════════════════════════════════════════════════
const ICON_MAP: Record<string, React.ElementType> = {
    'layout-dashboard': LayoutDashboard,
    'file-plus': FilePlus,
    'bar-chart-3': BarChart3,
    'calendar-range': Calendar,
    'book-open': BookOpen,
    'list-plus': ListPlus,
    'plug': Plug,
    'plug-zap': PlugZap,
    'download': Download,
    'share-2': Share2,
    'pie-chart': PieChart,
};

// ═══════════════════════════════════════════════════
// Props
// ═══════════════════════════════════════════════════
interface UpgradeModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** The feature key that triggered the limit (e.g. 'max_pages_per_board') */
    featureKey: string;
    /** Current usage count (for display: "You have X/Y") */
    currentUsage?: number;
}

// ═══════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════
export const UpgradeModal = ({
    isOpen,
    onClose,
    featureKey,
    currentUsage,
}: UpgradeModalProps) => {
    const { t } = useTranslation('common');
    const { getFeatureMeta, getFeatureValue, getPlanName, getPlanId } = usePlanFeaturesStore();
    const titleRef = useRef(null);

    const meta = getFeatureMeta(featureKey);
    const limit = getFeatureValue(featureKey);
    const planName = getPlanName();

    // Resolve icon
    const iconName = meta?.uiConfig?.icon || 'sparkles';
    const IconComponent = ICON_MAP[iconName] || Sparkles;

    // Resolve texts (from uiConfig with i18n fallbacks)
    const title = meta?.uiConfig?.upgradeTitle
        || t('upgrade.defaultTitle');
    const description = meta?.uiConfig?.upgradeDescription
        || t('upgrade.defaultDescription');

    // Shopify-billed users can't change plans in dashbal.com — every billing
    // action has to go through Shopify Admin (compliance + Shopify policy).
    // When the user is Shopify-billed, swap the upgrade CTA to a deep-link
    // into their Shopify subscriptions page so the action does something
    // useful instead of dropping them on the PlanPage compliance wall.
    const { data: billingStatusResponse } = useQuery({
        queryKey: ["subscriptions", "billing-status"],
        queryFn: subscriptionsService.getBillingStatus,
        retry: false,
        staleTime: 60 * 1000,
        enabled: isOpen,
    });
    const billingStatus = billingStatusResponse?.data;
    // Route the upgrade CTA to Shopify ONLY while a Shopify sub is active. Once
    // lapsed, the account is "open" → fall back to the normal website upgrade.
    const isShopifyBilled =
        billingStatus?.billingSource === "shopify" &&
        billingStatus?.billingStatus === "active";
    const shopifyManageUrl = isShopifyBilled
        ? buildShopifyManageBillingUrl(billingStatus?.shopifyBillingShop)
        : null;

    const handleUpgrade = () => {
        onClose();
        if (shopifyManageUrl) {
            window.open(shopifyManageUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        window.open('/plan', '_blank');
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[100]" onClose={onClose} initialFocus={titleRef}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl max-w-sm w-full p-8 transform transition-all border border-neutral-100 dark:border-neutral-800">
                                {/* Icon */}
                                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-accent-primary/10 rounded-full transition-colors duration-300">
                                    <IconComponent className="w-8 h-8 text-accent-primary" />
                                </div>

                                {/* Content */}
                                <div className="text-center mb-6">
                                    {/* Title */}
                                    <Dialog.Title
                                        as="h3"
                                        className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2 focus:outline-none"
                                        ref={titleRef}
                                        tabIndex={-1}
                                    >
                                        {title}
                                    </Dialog.Title>

                                    {/* Current plan badge */}
                                    <div className="mt-2 inline-flex items-center gap-1.5 h-6 px-3 rounded-full bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
                                        <Crown className="w-3 h-3 text-accent-primary" />
                                        <span className="text-[11px] font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                            {t('upgrade.currentPlan')}: {getPlanId() === 'free' ? t('upgrade.noSubscription') : planName}
                                        </span>
                                    </div>
                                </div>

                                <div className="text-center mb-8">
                                    {/* Usage indicator */}
                                    {typeof limit === 'number' && limit !== -1 && currentUsage !== undefined && (
                                        <div className="mb-4 p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                                                    {t('upgrade.usage')}
                                                </span>
                                                <span className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                                                    {currentUsage} / {limit}
                                                </span>
                                            </div>
                                            <div className="w-full h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full bg-accent-primary transition-all duration-500"
                                                    style={{ width: `${Math.min(100, (currentUsage / limit) * 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Description */}
                                    <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed text-center">
                                        {description}
                                    </p>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 mt-8">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onClose();
                                        }}
                                        className="flex-1 px-4 py-3 text-sm font-bold text-neutral-500 dark:text-neutral-400 bg-neutral-50 dark:bg-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-700 border border-neutral-200 dark:border-neutral-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-neutral-200 dark:focus:ring-neutral-700 transition-all active:scale-[0.98]"
                                    >
                                        {t('upgrade.dismiss')}
                                    </button>

                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleUpgrade();
                                        }}
                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-bold text-white bg-accent-primary hover:bg-accent-primary/90 rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-primary transition-all shadow-lg shadow-accent-primary/20 active:scale-[0.98]"
                                    >
                                        {isShopifyBilled ? <ShoppingBag className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                                        {isShopifyBilled
                                            ? t('billing.managedByShopify.openShopify', 'Manage in Shopify')
                                            : t('upgrade.cta')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
